<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- 遮罩层 -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <!-- 弹窗主体 -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-5xl bg-white rounded-2xl shadow-xl transform transition-all">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">加盟商详情</h3>
              <p v-if="franchiseeInfo" class="text-sm text-slate-500 mt-0.5">{{ franchiseeInfo.name }}</p>
            </div>
            <button
              @click="handleClose"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Tab 切换 -->
          <div class="flex-shrink-0 px-6 border-b border-slate-200">
            <nav class="flex gap-6">
              <button
                v-for="tab in tabs"
                :key="tab.key"
                @click="activeTab = tab.key"
                class="py-3 text-sm font-medium border-b-2 transition-colors"
                :class="activeTab === tab.key
                  ? 'text-primary-600 border-primary-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'"
              >
                {{ tab.label }}
                <span
                  v-if="tab.count !== undefined && tab.count !== null"
                  class="ml-1.5 px-1.5 py-0.5 text-xs rounded-full"
                  :class="activeTab === tab.key ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-600'"
                >
                  {{ tab.count }}
                </span>
              </button>
            </nav>
          </div>

          <!-- 内容区域 -->
          <div class="flex-1 overflow-y-auto max-h-[60vh]">
            <!-- 基本信息 Tab -->
            <div v-if="activeTab === 'basic'" class="p-6">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
                  <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-slate-900">{{ franchiseeInfo?.name }}</h4>
                  <p class="text-slate-500">{{ franchiseeInfo?.franchiseeNo || '-' }}</p>
                </div>
                <span :class="['ml-auto px-3 py-1 rounded-full text-sm font-medium', getStatusClass(franchiseeInfo?.status)]">
                  {{ franchiseeInfo?.statusDesc || getStatusText(franchiseeInfo?.status) }}
                </span>
              </div>

              <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">负责人</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.ownerUserName || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">所属部门</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.ownerDeptName || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">联系电话</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.phone || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">大区域</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.majorRegion || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">小区域</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.minorRegion || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">加盟等级</div>
                  <span :class="['px-2 py-1 rounded text-xs font-medium', getLevelClass(franchiseeInfo?.level)]">
                    {{ franchiseeInfo?.levelDesc || getLevelText(franchiseeInfo?.level) }}
                  </span>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">加盟日期</div>
                  <div class="font-medium text-slate-900">{{ formatDate(franchiseeInfo?.joinDate) }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">到期日期</div>
                  <div class="font-medium text-slate-900">{{ formatDate(franchiseeInfo?.expireDate) }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">保证金</div>
                  <div class="font-medium text-slate-900">¥{{ (franchiseeInfo?.deposit || 0).toLocaleString() }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4 md:col-span-3">
                  <div class="text-sm text-slate-500 mb-1">详细地址</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.address || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4 md:col-span-3">
                  <div class="text-sm text-slate-500 mb-1">备注</div>
                  <div class="font-medium text-slate-900">{{ franchiseeInfo?.remark || '-' }}</div>
                </div>
              </div>

              <!-- 业绩统计 -->
              <h5 class="text-lg font-medium text-slate-900 mt-6 mb-4">业绩统计</h5>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">客户数量</div>
                  <div class="text-xl font-bold text-slate-900">{{ franchiseeInfo?.customerCount || 0 }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">订单数量</div>
                  <div class="text-xl font-bold text-slate-900">{{ franchiseeInfo?.orderCount || 0 }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">业绩金额</div>
                  <div class="text-xl font-bold text-primary-600">¥{{ (franchiseeInfo?.performanceAmount || 0).toLocaleString() }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">最后回访</div>
                  <div class="text-xl font-bold text-slate-900">{{ formatDate(franchiseeInfo?.lastVisitTime) }}</div>
                </div>
              </div>
            </div>

            <!-- 业绩数据 Tab -->
            <div v-else-if="activeTab === 'performance'" class="p-6">
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">客户数量</div>
                  <div class="text-xl font-bold text-slate-900">{{ franchiseeInfo?.customerCount || 0 }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">订单数量</div>
                  <div class="text-xl font-bold text-slate-900">{{ franchiseeInfo?.orderCount || 0 }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">业绩金额</div>
                  <div class="text-xl font-bold text-primary-600">¥{{ (franchiseeInfo?.performanceAmount || 0).toLocaleString() }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">待回访</div>
                  <div class="text-xl font-bold text-slate-900">{{ franchiseeInfo?.nextVisitTime ? formatDate(franchiseeInfo.nextVisitTime) : '无' }}</div>
                </div>
              </div>
              <div class="h-64 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                业绩趋势图表区域
              </div>
            </div>

            <!-- 回访记录 Tab -->
            <div v-else-if="activeTab === 'visits'" class="p-6">
              <div class="mb-4 flex justify-between items-center">
                <h4 class="text-sm font-medium text-slate-700">回访记录</h4>
                <button
                  @click="openVisitModal"
                  class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 text-sm"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加回访
                </button>
              </div>

              <div v-if="visitsLoading" class="flex items-center justify-center py-12">
                <div class="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              </div>

              <div v-else-if="visits.length === 0" class="py-12 text-center text-slate-400">
                暂无回访记录
              </div>

              <div v-else class="space-y-3">
                <div v-for="visit in visits" :key="visit.id" class="border border-slate-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center gap-3">
                      <span :class="['px-2 py-0.5 rounded text-xs', getVisitTypeClass(visit.visitType)]">
                        {{ visit.visitTypeDesc || getVisitTypeText(visit.visitType) }}
                      </span>
                      <span class="text-sm text-slate-500">{{ formatDateTime(visit.visitTime) }}</span>
                    </div>
                    <span class="text-sm text-slate-500">回访人: {{ visit.visitUserName || '-' }}</span>
                  </div>
                  <div class="text-sm text-slate-700 mb-2">{{ visit.content }}</div>
                  <div v-if="visit.issues" class="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    问题/需求: {{ visit.issues }}
                  </div>
                  <div v-if="visit.nextVisitTime" class="mt-2 text-xs text-slate-500">
                    下次回访: {{ formatDateTime(visit.nextVisitTime) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 银行账户 Tab -->
            <div v-else-if="activeTab === 'bank'" class="p-6">
              <!-- 标题和按钮 -->
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-base font-medium text-slate-900">分账账户信息</h4>
                <div class="flex items-center gap-2">
                  <button
                    @click="openManualCompleteModal"
                    class="btn-secondary"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z"/>
                    </svg>
                    人工补录商户
                  </button>
                  <button
                    v-if="!franchiseeAccount || isLedgerNotApplied"
                    @click="handleCreateAccount"
                    class="btn-primary"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    {{ accountPrimaryActionText }}
                  </button>
                  <button
                    v-else
                    @click="handleEditAccount"
                    class="btn-primary"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                    编辑账户
                  </button>
                </div>
              </div>

              <!-- 加载状态 -->
              <div v-if="bankLoading" class="flex items-center justify-center py-12">
                <div class="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              </div>

              <!-- 账户信息 -->
              <div v-else-if="franchiseeAccount" class="space-y-6">
                <!-- 账户状态卡片 -->
                <div class="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                      </div>
                      <div>
                        <div class="text-sm text-primary-800">拉卡拉商户号</div>
                        <div class="text-lg font-semibold text-primary-900">{{ franchiseeAccount.merchantNo || franchiseeAccount.receiverNo || '-' }}</div>
                      </div>
                    </div>
                    <div class="flex flex-col items-end gap-2">
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="franchiseeAccount.bindStatus === 1 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'"
                      >
                        {{ franchiseeAccount.bindStatus === 1 ? '已绑定' : '未绑定' }}
                      </span>
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="franchiseeAccount.status === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      >
                        {{ franchiseeAccount.status === 0 ? '正常' : '禁用' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- 账户概要 -->
                <div class="grid grid-cols-3 gap-4">
                  <div class="bg-slate-50 rounded-lg p-4">
                    <div class="text-sm text-slate-500 mb-1">累计应收金额</div>
                    <div class="text-xl font-bold text-slate-900">¥{{ (franchiseeAccount.totalReceivable || 0).toLocaleString() }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-4">
                    <div class="text-sm text-slate-500 mb-1">累计已收金额</div>
                    <div class="text-xl font-bold text-green-600">¥{{ (franchiseeAccount.totalReceived || 0).toLocaleString() }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-4">
                    <div class="text-sm text-slate-500 mb-1">待收金额</div>
                    <div class="text-xl font-bold text-amber-600">¥{{ (franchiseeAccount.pendingAmount || 0).toLocaleString() }}</div>
                  </div>
                </div>

                <!-- 基本信息 -->
                <div class="bg-slate-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-slate-700 mb-4">基本信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-slate-500 mb-1">商户名称</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.payeeName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">联系电话</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.contactMobile || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">拉卡拉商户号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.merchantNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">拉卡拉终端号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.termNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">内部商户号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.merInnerNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户类型</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.acctTypeCode === '57' ? '对公账户' : franchiseeAccount.acctTypeCode === '58' ? '对私账户' : '-' }}</div>
                    </div>
                    <!-- <div>
                      <div class="text-xs text-slate-500 mb-1">提款类型</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.settleType === '01' ? '主动提款' : franchiseeAccount.settleType === '03' ? '交易自动结算' : '-' }}</div>
                    </div> -->
                  </div>
                </div>

                <!-- 收款账户信息 -->
                <div class="bg-slate-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-slate-700 mb-4">收款账户信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-slate-500 mb-1">收款账户名称</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.acctName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">收款账号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.acctNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">开户行名称</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.acctOpenBankName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">开户行号</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.acctOpenBankCode || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">清算行行号</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.acctClearBankCode || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户证件类型</div>
                      <div class="text-sm text-slate-900">{{ getCertificateTypeText(franchiseeAccount.acctCertificateType) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">账户证件号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.acctCertificateNo || '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 对公账户信息 -->
                <div v-if="franchiseeAccount.acctTypeCode === '57'" class="bg-amber-50 rounded-xl p-4">
                  <h4 class="text-sm font-medium text-amber-800 mb-4">营业执照及法人信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-amber-600 mb-1">营业执照号</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.licenseNo || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">营业执照名称</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.licenseName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人姓名</div>
                      <div class="text-sm text-slate-900">{{ franchiseeAccount.legalPersonName || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人证件类型</div>
                      <div class="text-sm text-slate-900">{{ getLegalCertTypeText(franchiseeAccount.legalPersonCertificateType) }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-amber-600 mb-1">法人证件号</div>
                      <div class="text-sm text-slate-900 font-mono">{{ franchiseeAccount.legalPersonCertificateNo || '-' }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 空状态 -->
              <div v-else class="flex flex-col items-center justify-center py-16 text-slate-500">
                <svg class="w-20 h-20 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
                <span class="text-lg mb-2">暂无分账账户</span>
                <span class="text-sm">请先完成拉卡拉商户进件，或人工补录已进件通过的商户信息</span>
                <button
                  @click="openManualCompleteModal"
                  class="btn-secondary mt-4"
                >
                  人工补录商户
                </button>
              </div>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button
              @click="handleClose"
              class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分账账户弹窗 -->
    <FranchiseeAccountFormModal
      v-model:visible="showAccountForm"
      :franchisee-id="franchiseeId"
      :account-data="editingAccount"
      @success="handleAccountSuccess"
    />

    <!-- 人工补录商户进件结果弹窗 -->
    <Teleport to="body">
      <div v-if="showManualCompleteModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeManualCompleteModal"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden transform transition-all">
            <!-- 头部 -->
            <div class="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">人工补录商户</h3>
                  <p class="text-xs text-slate-500">{{ franchiseeInfo?.name || '-' }}</p>
                </div>
              </div>
              <button
                @click="closeManualCompleteModal"
                class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6 space-y-4">
              <div class="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
                仅录入拉卡拉侧已完成进件后的商户结果信息。
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-700 mb-2">内部商户号 <span class="text-red-500">*</span></label>
                  <input
                    v-model.trim="manualCompleteForm.merInnerNo"
                    class="form-input w-full font-mono"
                    placeholder="请输入拉卡拉内部商户号"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">银联商户号 <span class="text-red-500">*</span></label>
                  <input
                    v-model.trim="manualCompleteForm.merchantNo"
                    class="form-input w-full font-mono"
                    placeholder="请输入银联商户号"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">终端号 <span class="text-red-500">*</span></label>
                  <input
                    v-model.trim="manualCompleteForm.termNo"
                    class="form-input w-full font-mono"
                    placeholder="请输入拉卡拉终端号"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">进件ID</label>
                  <input
                    v-model.trim="manualCompleteForm.contractId"
                    class="form-input w-full"
                    placeholder="可选"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">机构代码</label>
                  <input
                    v-model.trim="manualCompleteForm.orgCode"
                    class="form-input w-full"
                    placeholder="可选"
                  />
                </div>
              </div>
            </div>

            <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                @click="closeManualCompleteModal"
                class="btn-secondary"
                :disabled="manualCompleteSubmitting"
              >
                取消
              </button>
              <button
                @click="handleManualCompleteSubmit"
                class="btn-primary"
                :disabled="manualCompleteSubmitting"
              >
                <svg v-if="manualCompleteSubmitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                保存补录
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 添加回访弹窗 -->
    <Teleport to="body">
      <div v-if="showVisitModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="showVisitModal = false"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all">
            <!-- 头部 -->
            <div class="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">添加回访记录</h3>
                  <p class="text-xs text-slate-500">记录回访信息</p>
                </div>
              </div>
              <button @click="showVisitModal = false" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6">
              <div class="space-y-4">
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">回访方式 <span class="text-red-500">*</span></label>
                  <select v-model="visitForm.visitType" class="form-select-native w-full">
                    <option :value="0">电话</option>
                    <option :value="1">微信</option>
                    <option :value="2">上门</option>
                    <option :value="3">邮件</option>
                    <option :value="4">其他</option>
                  </select>
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">回访内容 <span class="text-red-500">*</span></label>
                  <textarea v-model="visitForm.content" rows="4" class="form-textarea" placeholder="请输入回访内容"></textarea>
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">回访时间</label>
                  <el-date-picker v-model="visitForm.visitTime" type="datetime" class="form-date" format="YYYY年MM月DD日 HH:mm" value-format="YYYY-MM-DD HH:mm:ss" placeholder="请选择回访时间" />
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">问题/需求记录</label>
                  <textarea v-model="visitForm.issues" rows="2" class="form-textarea" placeholder="请输入问题和需求"></textarea>
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">下次回访时间</label>
                  <el-date-picker v-model="visitForm.nextVisitTime" type="datetime" class="form-date" format="YYYY年MM月DD日 HH:mm" value-format="YYYY-MM-DD HH:mm:ss" placeholder="请选择下次回访时间" />
                </div>
              </div>
            </div>

            <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button @click="showVisitModal = false" class="btn-secondary">取消</button>
              <button @click="handleCreateVisit" :loading="visitSubmitting" class="btn-primary">
                <svg v-if="visitSubmitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                保存
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getFranchiseeVisitList, createFranchiseeVisit, manualCompleteLakalaMerchantApply } from '@/api'
import { getFranchiseeAccount } from '@/api/commission'
import FranchiseeAccountFormModal from './FranchiseeAccountFormModal.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  franchiseeId: {
    type: [Number, String],
    default: null
  },
  franchiseeInfo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible'])

// Tab 配置
const tabs = computed(() => [
  { key: 'basic', label: '基本信息', count: null },
  { key: 'performance', label: '业绩数据', count: null },
  { key: 'visits', label: '回访记录', count: visits.value.length },
  { key: 'bank', label: '分账账户', count: franchiseeAccount.value ? 1 : null }
])

const isLedgerNotApplied = computed(() => {
  const account = franchiseeAccount.value
  return !!account && !account.applyId && (!account.ledgerStatus || account.ledgerStatus === 'NOT_APPLIED')
})

const accountPrimaryActionText = computed(() => (
  franchiseeAccount.value ? '申请开通分账' : '补录商户信息'
))

const activeTab = ref('basic')

// 状态映射
const statusTextMap = {
  0: '潜在加盟商',
  1: '意向加盟商',
  2: '签约审核中',
  3: '正式加盟商',
  4: '暂停',
  5: '终止'
}

const levelTextMap = {
  0: '普通加盟商',
  1: '银牌加盟商',
  2: '金牌加盟商',
  3: '钻石加盟商'
}

const visitTypeTextMap = {
  0: '电话',
  1: '微信',
  2: '上门',
  3: '邮件',
  4: '其他'
}

// 回访记录
const visits = ref([])
const visitsLoading = ref(false)

// 银行账户
const franchiseeAccount = ref(null)
const bankLoading = ref(false)
const showAccountForm = ref(false)
const editingAccount = ref(null)
const showManualCompleteModal = ref(false)
const manualCompleteSubmitting = ref(false)
const manualCompleteForm = reactive({
  merInnerNo: '',
  merchantNo: '',
  termNo: '',
  contractId: '',
  orgCode: ''
})

// 回访弹窗
const showVisitModal = ref(false)
const visitSubmitting = ref(false)
const visitForm = reactive({
  visitType: 0,
  content: '',
  visitTime: '',
  nextVisitTime: '',
  issues: ''
})

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('zh-CN')
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN')
}

// 获取状态文本
const getStatusText = (status) => {
  return statusTextMap[status] || '未知'
}

// 获取等级文本
const getLevelText = (level) => {
  return levelTextMap[level] || '未知'
}

// 获取回访方式文本
const getVisitTypeText = (type) => {
  return visitTypeTextMap[type] || '未知'
}

// 获取状态样式
const getStatusClass = (status) => {
  const classes = {
    0: 'bg-slate-100 text-slate-700',
    1: 'bg-blue-100 text-blue-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-green-100 text-green-700',
    4: 'bg-red-100 text-red-700',
    5: 'bg-slate-100 text-slate-600'
  }
  return classes[status] || 'bg-slate-100 text-slate-600'
}

// 获取等级样式
const getLevelClass = (level) => {
  const classes = {
    0: 'bg-slate-100 text-slate-600',
    1: 'bg-slate-200 text-slate-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-purple-100 text-purple-700'
  }
  return classes[level] || 'bg-slate-100 text-slate-600'
}

// 获取回访方式样式
const getVisitTypeClass = (type) => {
  const classes = {
    0: 'bg-blue-100 text-blue-700',
    1: 'bg-green-100 text-green-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-slate-100 text-slate-700',
    4: 'bg-slate-100 text-slate-600'
  }
  return classes[type] || 'bg-slate-100 text-slate-600'
}

// 加载回访记录
const loadVisits = async () => {
  if (!props.franchiseeId) return
  visitsLoading.value = true
  try {
    visits.value = await getFranchiseeVisitList(props.franchiseeId)
  } catch (error) {
    console.error('获取回访记录失败:', error)
    visits.value = []
  } finally {
    visitsLoading.value = false
  }
}

// 加载加盟商分账账户
const loadFranchiseeAccount = async () => {
  if (!props.franchiseeId) return
  bankLoading.value = true
  try {
    franchiseeAccount.value = await getFranchiseeAccount(props.franchiseeId)
  } catch (error) {
    console.error('获取分账账户失败:', error)
    franchiseeAccount.value = null
  } finally {
    bankLoading.value = false
  }
}

// 创建账户
const handleCreateAccount = () => {
  if (!franchiseeAccount.value) {
    ElMessage.warning('请先完成拉卡拉商户进件或人工补录商户信息')
    openManualCompleteModal()
    return
  }
  editingAccount.value = isLedgerNotApplied.value ? { ...franchiseeAccount.value } : null
  showAccountForm.value = true
}

// 编辑账户
const handleEditAccount = () => {
  editingAccount.value = { ...franchiseeAccount.value }
  showAccountForm.value = true
}

// 账户操作成功
const handleAccountSuccess = () => {
  loadFranchiseeAccount()
}

// 人工补录商户进件结果
const resetManualCompleteForm = () => {
  Object.assign(manualCompleteForm, {
    merInnerNo: franchiseeAccount.value?.merInnerNo || '',
    merchantNo: franchiseeAccount.value?.merchantNo || '',
    termNo: franchiseeAccount.value?.termNo || '',
    contractId: '',
    orgCode: franchiseeAccount.value?.orgCode || ''
  })
}

const openManualCompleteModal = () => {
  resetManualCompleteForm()
  showManualCompleteModal.value = true
}

const closeManualCompleteModal = () => {
  if (manualCompleteSubmitting.value) return
  showManualCompleteModal.value = false
}

const buildManualCompletePayload = () => {
  return Object.entries(manualCompleteForm).reduce((payload, [key, value]) => {
    if (typeof value === 'string' && value.trim()) {
      payload[key] = value.trim()
    }
    return payload
  }, {})
}

const handleManualCompleteSubmit = async () => {
  if (!props.franchiseeId) {
    ElMessage.warning('缺少加盟商ID')
    return
  }
  if (!manualCompleteForm.merInnerNo.trim()) {
    ElMessage.warning('请输入拉卡拉内部商户号')
    return
  }
  if (!manualCompleteForm.merchantNo.trim()) {
    ElMessage.warning('请输入银联商户号')
    return
  }
  if (!manualCompleteForm.termNo.trim()) {
    ElMessage.warning('请输入拉卡拉终端号')
    return
  }

  manualCompleteSubmitting.value = true
  try {
    await manualCompleteLakalaMerchantApply(props.franchiseeId, buildManualCompletePayload())
    ElMessage.success('人工补录成功')
    showManualCompleteModal.value = false
    loadFranchiseeAccount()
  } catch (error) {
    console.error('人工补录商户信息失败:', error)
    ElMessage.error(error.message || '人工补录失败')
  } finally {
    manualCompleteSubmitting.value = false
  }
}

// 获取证件类型文本
const getCertificateTypeText = (type) => {
  const map = { '01': '身份证', '02': '营业执照', '03': '统一社会信用代码' }
  return map[type] || '-'
}

// 获取法人证件类型文本
const getLegalCertTypeText = (type) => {
  const map = { '01': '身份证', '02': '护照' }
  return map[type] || '-'
}

// 打开回访弹窗
const openVisitModal = () => {
  Object.assign(visitForm, {
    visitType: 0,
    content: '',
    visitTime: '',
    nextVisitTime: '',
    issues: ''
  })
  showVisitModal.value = true
}

// 创建回访记录
const handleCreateVisit = async () => {
  if (!visitForm.content.trim()) {
    ElMessage.warning('请输入回访内容')
    return
  }

  visitSubmitting.value = true
  try {
    await createFranchiseeVisit({
      franchiseeId: props.franchiseeId,
      ...visitForm
    })
    ElMessage.success('回访记录已保存')
    showVisitModal.value = false
    loadVisits()
  } catch (error) {
    console.error('创建回访记录失败:', error)
    ElMessage.error(error.message || '创建回访记录失败')
  } finally {
    visitSubmitting.value = false
  }
}

// 关闭弹窗
const handleClose = () => {
  showManualCompleteModal.value = false
  emit('update:visible', false)
}

// 监听 Tab 切换
watch(activeTab, (tab) => {
  if (tab === 'visits' && visits.value.length === 0) {
    loadVisits()
  } else if (tab === 'bank' && !franchiseeAccount.value) {
    loadFranchiseeAccount()
  }
})

// 监听弹窗显示
watch(() => props.visible, (val) => {
  if (val) {
    // 重置状态
    visits.value = []
    franchiseeAccount.value = null
    showManualCompleteModal.value = false
    activeTab.value = 'basic'
  }
})
</script>
